/**
 * @module useMessages
 * @description Custom hook for managing messages and categories in the mental health portal.
 * Provides functionality for CRUD operations on messages and categories, form handling,
 * and UI state management.
 */

/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import api from '../../services/api';

/**
 * @function useMessages
 * @description Custom hook for managing messages and categories in the mental health portal
 * @returns {Object} Object containing state variables and methods for message and category management:
 * @returns {boolean} isLoading - Indicates if data is being loaded
 * @returns {Array} messages - List of all messages across all categories
 * @returns {Function} selectMessage - Function to select a message by ID
 * @returns {Function} setSelectedMessage - Function to directly set the selected message
 * @returns {Object|null} selectedMessage - Currently selected message
 * @returns {Function} handleNewMessageForm - Opens form for creating a new message
 * @returns {Function} setFilteredMessages - Function to directly set filtered messages
 * @returns {Array} filteredMessages - Messages filtered by the selected category
 * @returns {boolean} isOpenMessageForm - Indicates if message form is open
 * @returns {Function} handleOpenMessageForm - Function to open the message form
 * @returns {Function} handleEditMessageForm - Opens form for editing a message
 * @returns {Object} formMessageData - Current data in the message form
 * @returns {Function} updateFormMessageField - Updates a field in the message form
 * @returns {Function} handleSaveMessageForm - Saves a new message
 * @returns {Function} handleUpdateMessageForm - Updates an existing message
 * @returns {Function} handleCloseMessageForm - Closes the message form
 * @returns {boolean} isOpenDeleteMessageModal - Indicates if delete message modal is open
 * @returns {Function} handleOpenDeleteMessageModal - Opens the delete message modal
 * @returns {Function} handleCloseDeleteMessageModal - Closes the delete message modal
 * @returns {Function} handlerDeleteMessageModal - Handles message deletion
 * @returns {boolean} isValidFormMessage - Indicates if message form is valid
 * @returns {Object} errorsFormMessage - Contains validation errors for message form
 * @returns {Array} categories - List of all message categories
 * @returns {Object|null} selectedCategory - Currently selected category
 * @returns {Function} selectCategory - Function to select a category by ID
 * @returns {boolean} isOpenCategoryForm - Indicates if category form is open
 * @returns {Function} handleNewCategoryForm - Opens form for creating a new category
 * @returns {Function} handleOpenCategoryFormModal - Opens the category form modal
 * @returns {Function} handleCloseCategoryFormModal - Closes the category form modal
 * @returns {Function} updateFormCategoryField - Updates a field in the category form
 * @returns {Function} handleSaveCategoryForm - Saves a new category
 * @returns {Function} handleUpdateCategoryForm - Updates an existing category
 * @returns {Function} handleEditCategoryForm - Opens form for editing a category
 * @returns {Object} formCategoryData - Current data in the category form
 * @returns {boolean} isOpenDeleteCategoryForm - Indicates if delete category modal is open
 * @returns {Function} handleOpenDeleteCategoryFormModal - Opens the delete category modal
 * @returns {Function} handleCloseDeleteCategoryFormModal - Closes the delete category modal
 * @returns {Function} handleDeleteCategoryForm - Handles category deletion
 * @returns {boolean} isValidFormCategory - Indicates if category form is valid
 * @returns {Object} errorsFormCategory - Contains validation errors for category form
 * @returns {boolean} showAlert - Indicates if alert is visible
 * @returns {Function} handleCloseAlert - Closes the alert
 * @returns {string} alertMessage - Message to display in the alert
 * @returns {string} typeAlert - Type of alert (success, error, etc.)
 */
export const useMessages = () => {
  const { t } = useTranslation();

  const auth = useAuth();
  const authId = auth.user?.profile.sub;

  /**
   * @constant {Object} initialState
   * @description Initial state for the hook
   */
  const initialState = {
    isLoading: true,
    messages: [],
    selectedMessage: null,
    filteredMessages: [],
    isOpenMessageForm: false,
    isOpenDeleteMessageModal: false,
    formMessageData: {
      id: null,
      category_id: null,
      name: null,
      message: null,
      created_by: null,
      updated_by: null,
    },
    isValidFormMessage: true,
    errorsFormMessage: {
      name: false,
      category_id: false,
      message: false,
    },
    categories: [],
    isGetCategories: false,
    selectedCategory: null,
    isOpenCategoryForm: false,
    isOpenDeleteCategoryForm: false,
    formCategoryData: {
      id: null,
      name: null,
      created_by: null,
      updated_by: null,
      created_at: null,
      updated_at: null,
    },
    isValidFormCategory: true,
    errorsFormCategory: {
      name: false,
    },
    showAlert: false,
    typeAlert: 'success',
    alertMessage: '',
  };

  // General states
  const [isLoading, setIsLoading] = useState(initialState.isLoading);
  const [showAlert, setShowAlert] = useState(initialState.showAlert);
  const [alertMessage, setAlertMessage] = useState(initialState.alertMessage);
  const [typeAlert, setTypeAlert] = useState(initialState.typeAlert);

  // MESSAGES STATES
  const [messages, setMessages] = useState(initialState.messages);
  const [selectedMessage, setSelectedMessage] = useState(
    initialState.selectedMessage
  );
  const [filteredMessages, setFilteredMessages] = useState(
    initialState.filteredMessages
  );
  const [isOpenMessageForm, setIsOpenMessageForm] = useState(
    initialState.isOpenMessageForm
  );
  const [isOpenDeleteMessageModal, setIsOpenDeleteMessageModal] = useState(
    initialState.isOpenDeleteMessageModal
  );
  const [formMessageData, setFormMessageData] = useState(
    initialState.formMessageData
  );
  const [isValidFormMessage, setIsValidFormMessage] = useState(
    initialState.isValidFormMessage
  );
  const [errorsFormMessage, setErrorsFormMessage] = useState(
    initialState.errorsFormMessage
  );

  // CATEGORIES STATES
  const [categories, setCategories] = useState(initialState.categories);
  const [isGetCategories, setIsGetCategories] = useState(
    initialState.isGetCategories
  );
  const [selectedCategory, setSelectedCategory] = useState(
    initialState.selectedCategory
  );
  const [isOpenCategoryForm, setIsOpenCategoryForm] = useState(
    initialState.isOpenCategoryForm
  );
  const [isOpenDeleteCategoryForm, setIsOpenDeleteCategoryForm] = useState(
    initialState.isOpenCategoryForm
  );
  const [formCategoryData, setFormCategoryData] = useState(
    initialState.formCategoryData
  );
  const [isValidFormCategory, setIsValidFormCategory] = useState(
    initialState.isValidFormCategory
  );
  const [errorsFormCategory, setErrorsFormCategory] = useState(
    initialState.errorsFormCategory
  );

  /**
   * @effect
   * @description Fetch categories on component mount
   */
  useEffect(() => {
    const fetchData = async () => {
      await readCategories();
    };
    fetchData();
  }, []);

  /**
   * @effect
   * @description Fetch messages when categories are loaded
   */
  useEffect(() => {
    const fetchData = async () => {
      await readMessages();

      if (categories.length > 0) {
        setSelectedCategory(categories[0]);
      } else {
        setIsLoading(false);
      }
    };

    if (isGetCategories) fetchData();
  }, [categories]);

  /**
   * @effect
   * @description Filter messages when selected category changes
   */
  useEffect(() => {
    filterMessagesByCategory(selectedCategory?.id);
  }, [selectedCategory]);

  /**
   * @effect
   * @description Update selected message when filtered messages change
   */
  useEffect(() => {
    if (filteredMessages?.length > 0 && filteredMessages[0]) {
      setSelectedMessage(filteredMessages[0]);
    } else {
      setSelectedMessage(null);
    }

    if (isGetCategories) {
      setIsLoading(false);
      setIsGetCategories(false);
    }
  }, [filteredMessages]);

  /**
   * @function handleCloseAlert
   * @description Close the alert and reset alert message
   */
  const handleCloseAlert = () => {
    setShowAlert(false);
    setAlertMessage(initialState.alertMessage);
  };

  // MESSAGES METHODS

  /**
   * @function selectMessage
   * @description Select a message by ID and update the selected message state
   * @param {string|number} messageId - ID of the message to select
   * @returns {Object|null} The selected message object or null if not found
   */
  const selectMessage = (messageId) => {
    const message = filteredMessages.find(
      (message) => message.id === messageId
    );

    setSelectedMessage(message);
    return message;
  };

  /**
   * @function resetFormMessageData
   * @description Reset the message form data to initial state
   */
  const resetFormMessageData = () => {
    setFormMessageData(initialState.formMessageData);
  };

  /**
   * @function handleOpenMessageForm
   * @description Open the message form
   */
  const handleOpenMessageForm = () => {
    setIsOpenMessageForm(true);
  };

  /**
   * @function handleNewMessageForm
   * @description Reset form data and open the message form for a new message
   */
  const handleNewMessageForm = () => {
    resetFormMessageData();
    handleOpenMessageForm();
  };

  /**
   * @function handleEditMessageForm
   * @description Select a message and open the form for editing
   * @param {string|number} messageId - ID of the message to edit
   */
  const handleEditMessageForm = (messageId) => {
    const message = selectMessage(messageId);
    setFormMessageData({ ...message });
    handleOpenMessageForm();
  };

  /**
   * @function updateFormMessageField
   * @description Update a specific field in the message form
   * @param {string} field - Field name to update
   * @param {*} value - New value for the field
   */
  const updateFormMessageField = (field, value) => {
    setFormMessageData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  /**
   * @function handleCloseMessageForm
   * @description Close the message form and reset form state
   */
  const handleCloseMessageForm = () => {
    resetFormMessageData();
    setIsValidFormMessage(initialState.isValidFormMessage);
    setErrorsFormMessage(initialState.errorsFormMessage);
    setIsOpenMessageForm(false);
  };

  /**
   * @function handleSaveMessageForm
   * @description Validate and save a new message
   * @param {Object} messageData - Message data to save
   */
  const handleSaveMessageForm = async (messageData) => {
    try {
      const isValid = validFormMessage();

      if (isValid) {
        setIsLoading(true);
        await saveMessage(messageData);
        await readCategories();
        handleCloseMessageForm();
        setTypeAlert('success');
        setAlertMessage(t('message created successfully'));
        setShowAlert(true);
        return;
      }
    } catch (error) {
      console.error(`handleSaveMessageForm: ${error.message}`);
      handleCloseMessageForm();
      setTypeAlert('error');
      setAlertMessage(t('error creating message'));
      setShowAlert(true);
      setIsLoading(false);
    }
  };

  /**
   * @function handleUpdateMessageForm
   * @description Validate and update an existing message
   * @param {Object} messageData - Message data to update
   */
  const handleUpdateMessageForm = async (messageData) => {
    try {
      const isValid = validFormMessage();

      if (isValid) {
        setIsLoading(true);
        await updateMessage(messageData);
        await readCategories();
        handleCloseMessageForm();
        setTypeAlert('success');
        setAlertMessage(t('message updated successfully'));
        setShowAlert(true);
        return;
      }
    } catch (error) {
      console.error(`handleUpdateMessageForm: ${error.message}`);
      handleCloseMessageForm();
      setTypeAlert('error');
      setAlertMessage(t('error updating message'));
      setShowAlert(true);
      setIsLoading(false);
    }
  };

  /**
   * @function handleOpenDeleteMessageModal
   * @description Open the delete message confirmation modal
   */
  const handleOpenDeleteMessageModal = () => {
    setIsOpenDeleteMessageModal(true);
  };

  /**
   * @function handleCloseDeleteMessageModal
   * @description Close the delete message confirmation modal
   */
  const handleCloseDeleteMessageModal = () => {
    setIsOpenDeleteMessageModal(false);
  };

  /**
   * @function handlerDeleteMessageModal
   * @description Delete a message and handle the UI updates
   * @param {string|number} messageId - ID of the message to delete
   */
  const handlerDeleteMessageModal = async (messageId) => {
    try {
      setIsLoading(true);
      await deleteMessage(messageId);
      await readCategories();
      setTypeAlert('success');
      setAlertMessage(t('message deletion successful'));
      setShowAlert(true);
      handleCloseDeleteMessageModal();
    } catch (error) {
      console.error(`handlerDeleteMessageModal: ${error.message}`);
      handleCloseDeleteMessageModal();
      setTypeAlert('error');
      setAlertMessage(t('error deleting message'));
      setShowAlert(true);
      setIsLoading(false);
    }
  };

  /**
   * @function readMessages
   * @description Fetch all messages from the API for all categories
   * @param {string} [authId] - User authentication ID
   */
  const readMessages = async (authId) => {
    const allMessages = [];
    const limit = 1;

    for await (const category of categories) {
      let hasNextPage = true;
      let currentOffset = 0;

      while (hasNextPage) {
        const { data } = await api.get(
          `messages?categoryId=${category.id}&limit=${limit}&offset=${currentOffset}`
        );

        const { messages, pagination } = data;

        allMessages.push(...messages);

        hasNextPage = pagination.hasNextPage;
        currentOffset += limit;
      }
    }

    setMessages(allMessages);
  };

  /**
   * @function saveMessage
   * @description Save a new message to the API
   * @param {Object} messageData - Message data to save
   */
  const saveMessage = async (messageData) => {
    await api.post('create-messages', {
      category_id: messageData.category_id,
      name: messageData.name,
      message: messageData.message,
      userId: authId,
    });
  };

  /**
   * @function updateMessage
   * @description Update an existing message via the API
   * @param {Object} messageData - Message data to update
   */
  const updateMessage = async (messageData) => {
    await api.put(`message/${messageData.id}`, {
      id_category: messageData.category_id,
      name: messageData.name,
      message: messageData.message,
      userId: authId,
    });
  };

  /**
   * @function deleteMessage
   * @description Delete a message via the API
   * @param {string|number} messageId - ID of the message to delete
   */
  const deleteMessage = async (messageId) => {
    await api.delete(`message/${messageId}`);
  };

  /**
   * @function validFormMessage
   * @description Validate the message form data
   * @returns {boolean} True if form is valid, false otherwise
   */
  const validFormMessage = () => {
    const errors = initialState.errorsFormMessage;

    const { name, message, category_id } = formMessageData;

    if (!name) errors.name = true;
    if (!message) errors.message = true;
    if (!category_id) errors.category_id = true;

    if (!name || !message || !category_id) {
      setErrorsFormMessage(errors);
      setIsValidFormMessage(false);
      setTypeAlert('error');
      setAlertMessage(t('Please fill in all required fields.'));
      setShowAlert(true);
      return false;
    }

    setIsValidFormMessage(true);
    setErrorsFormMessage(initialState.errorsFormMessage);
    setTypeAlert(initialState.typeAlert);
    setShowAlert(initialState.showAlert);
    return true;
  };

  // CATEGORIES METHODS
  /**
   * @function selectCategory
   * @description Select a category by ID and update the selected category state
   * @param {string|number} categoryId - ID of the category to select
   * @returns {Object|null} The selected category object or null if not found
   */
  const selectCategory = (categoryId) => {
    const category = categories.find((cat) => cat.id === categoryId);

    if (!categoryId || !category) {
      setSelectedCategory(null);
      setSelectedMessage(null);

      return null;
    }

    if (category) {
      setSelectedCategory(category);
    }

    return category;
  };

  /**
   * @function filterMessagesByCategory
   * @description Filter messages by category ID
   * @param {string|number} categoryId - ID of the category to filter by
   * @returns {Array|null} Filtered messages array or null if no category ID
   */
  const filterMessagesByCategory = (categoryId) => {
    if (!categoryId) {
      setFilteredMessages(null);
      return null;
    }

    const filteredMessages = messages.filter(
      (q) => q.category_id === categoryId
    );

    filteredMessages.sort(
      (a, b) => new Date(b.updated_at) - new Date(a.updated_at)
    );

    setFilteredMessages(filteredMessages);

    return filteredMessages;
  };

  /**
   * @function resetFormCategoryData
   * @description Reset the category form data to initial state
   */
  const resetFormCategoryData = () => {
    setFormCategoryData(initialState.formCategoryData);
  };

  /**
   * @function handleOpenCategoryFormModal
   * @description Open the category form modal
   */
  const handleOpenCategoryFormModal = () => {
    setIsOpenCategoryForm(true);
  };

  /**
   * @function handleCloseCategoryFormModal
   * @description Close the category form modal and reset form state
   */
  const handleCloseCategoryFormModal = () => {
    resetFormCategoryData();
    setIsValidFormCategory(true);
    setErrorsFormCategory(initialState.errorsFormCategory);
    setShowAlert(initialState.showAlert);
    setIsOpenCategoryForm(false);
  };

  /**
   * @function handleNewCategoryForm
   * @description Reset form data and open the category form for a new category
   */
  const handleNewCategoryForm = () => {
    resetFormCategoryData();
    handleOpenCategoryFormModal();
  };

  /**
   * @function handleEditCategoryForm
   * @description Select a category and open the form for editing
   * @param {string|number} categoryId - ID of the category to edit
   */
  const handleEditCategoryForm = (categoryId) => {
    const category = selectCategory(categoryId);

    if (category) {
      setFormCategoryData({ ...category });
      handleOpenCategoryFormModal();
    }
  };

  /**
   * @function updateFormCategoryField
   * @description Update a specific field in the category form
   * @param {string} field - Field name to update
   * @param {*} value - New value for the field
   */
  const updateFormCategoryField = (field, value) => {
    setFormCategoryData((prev) => ({ ...prev, [field]: value }));
  };

  /**
   * @function handleSaveCategoryForm
   * @description Validate and save a new category
   * @param {Object} categoryData - Category data to save
   */
  const handleSaveCategoryForm = async (categoryData) => {
    try {
      const isValid = validFormCategory();

      if (isValid) {
        setIsLoading(true);
        await saveCategory(categoryData, authId);
        await readCategories();
        handleCloseCategoryFormModal();
        setTypeAlert('success');
        setAlertMessage(t('category created successfully'));
        setShowAlert(true);
        return;
      }
    } catch (error) {
      console.error(`handleSaveCategoryForm: ${error.message}`);
      handleCloseCategoryFormModal();
      setTypeAlert('error');
      setAlertMessage(t('error creating category'));
      setShowAlert(true);
      setIsLoading(false);
    }
  };

  /**
   * @function handleUpdateCategoryForm
   * @description Validate and update an existing category
   * @param {Object} categoryData - Category data to update
   */
  const handleUpdateCategoryForm = async (categoryData) => {
    try {
      const isValid = validFormCategory();

      if (isValid) {
        setIsLoading(true);
        await updateCategory(categoryData, authId);
        await readCategories();
        handleCloseCategoryFormModal();
        setTypeAlert('success');
        setAlertMessage(t('category updated successfully'));
        setShowAlert(true);
      }
    } catch (error) {
      console.error(`handleUpdateCategoryForm: ${error.message}`);
      handleCloseCategoryFormModal();
      setTypeAlert('error');
      setAlertMessage(t('error updating category'));
      setShowAlert(true);
      setIsLoading(false);
    }
  };

  /**
   * @function handleOpenDeleteCategoryFormModal
   * @description Open the delete category confirmation modal
   */
  const handleOpenDeleteCategoryFormModal = () => {
    setIsOpenDeleteCategoryForm(true);
  };

  /**
   * @function handleCloseDeleteCategoryFormModal
   * @description Close the delete category confirmation modal
   */
  const handleCloseDeleteCategoryFormModal = () => {
    resetFormCategoryData();
    setIsOpenDeleteCategoryForm(false);
  };

  /**
   * @function handleDeleteCategoryForm
   * @description Delete a category and handle the UI updates
   * @param {string|number} categoryId - ID of the category to delete
   */
  const handleDeleteCategoryForm = async (categoryId) => {
    try {
      setIsLoading(true);
      await deleteCategory(categoryId);
      await readCategories();
      setTypeAlert('success');
      setAlertMessage(t('category deletion successful'));
      setShowAlert(true);
      handleCloseDeleteCategoryFormModal();
    } catch (error) {
      console.error(`handleDeleteCategoryForm: ${error.message}`);
      handleCloseDeleteCategoryFormModal();
      setTypeAlert('error');
      setAlertMessage(t('error deleting category'));
      setShowAlert(true);
      setIsLoading(false);
    }
  };

  /**
   * @function readCategories
   * @description Fetch all categories from the API
   */
  const readCategories = async () => {
    try {
      let categoriesWithAmount = [];

      const { data } = await api.get('message-categories');
      const { messageCategories, numMessages } = data;

      if (messageCategories.length > 0) {
        categoriesWithAmount = messageCategories.map((message) => {
          const categoryNumMessages = numMessages.find(
            (categoryNumMessage) => categoryNumMessage.categoryId === message.id
          );

          if (categoryNumMessages) {
            return {
              ...message,
              amount: categoryNumMessages.amountMessages,
            };
          }

          return { ...message, amount: 0 };
        });

        categoriesWithAmount.sort((a, b) => a.id - b.id);

        setCategories(categoriesWithAmount);
        setIsGetCategories(true);
      }
    } catch (error) {
      console.error('readCategories', error.message);
      setCategories([]);
      setIsGetCategories(true);
    }
  };

  /**
   * @function saveCategory
   * @description Save a new category to the API
   * @param {Object} categoryData - Category data to save
   * @param {string} authId - User authentication ID
   */
  const saveCategory = async (categoryData, authId) => {
    await api.post('message-categories', {
      name: categoryData.name,
      userId: authId,
    });
  };

  /**
   * @function validFormCategory
   * @description Validate the category form data
   * @returns {boolean} True if form is valid, false otherwise
   */
  const validFormCategory = () => {
    const errors = initialState.errorsFormCategory;

    if (!formCategoryData.name) {
      errors.name = true;
      setErrorsFormCategory(errors);
      setIsValidFormCategory(false);
      setTypeAlert('error');
      setAlertMessage(t('Please fill in all required fields.'));
      setShowAlert(true);
      return false;
    }

    setIsValidFormCategory(true);
    setErrorsFormCategory(initialState.errorsFormCategory);
    setTypeAlert(initialState.typeAlert);
    setShowAlert(initialState.showAlert);
    return true;
  };

  /**
   * @function updateCategory
   * @description Update an existing category via the API
   * @param {Object} categoryData - Category data to update
   * @param {string} authId - User authentication ID
   */
  const updateCategory = async (categoryData, authId) => {
    await api.put(`message-categories/${categoryData.id}`, {
      name: categoryData.name,
      userId: authId,
    });
  };

  /**
   * @function deleteCategory
   * @description Delete a category via the API
   * @param {string|number} categoryId - ID of the category to delete
   */
  const deleteCategory = async (categoryId) => {
    await api.delete(`message-categories/${categoryId}`);
  };

  /**
   * @returns {Object} Object containing state variables and methods for message and category management
   */
  return {
    isLoading,
    messages,
    selectMessage,
    setSelectedMessage,
    selectedMessage,
    handleNewMessageForm,
    setFilteredMessages,
    filteredMessages,
    isOpenMessageForm,
    handleOpenMessageForm,
    handleEditMessageForm,
    formMessageData,
    updateFormMessageField,
    handleSaveMessageForm,
    handleUpdateMessageForm,
    handleCloseMessageForm,
    isOpenDeleteMessageModal,
    handleOpenDeleteMessageModal,
    handleCloseDeleteMessageModal,
    handlerDeleteMessageModal,
    isValidFormMessage,
    errorsFormMessage,
    categories,
    selectedCategory,
    selectCategory,
    isOpenCategoryForm,
    handleNewCategoryForm,
    handleOpenCategoryFormModal,
    handleCloseCategoryFormModal,
    updateFormCategoryField,
    handleSaveCategoryForm,
    handleUpdateCategoryForm,
    handleEditCategoryForm,
    formCategoryData,
    isOpenDeleteCategoryForm,
    handleOpenDeleteCategoryFormModal,
    handleCloseDeleteCategoryFormModal,
    handleDeleteCategoryForm,
    isValidFormCategory,
    errorsFormCategory,
    showAlert,
    handleCloseAlert,
    alertMessage,
    typeAlert,
  };
};
